import { useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import "./tenant-profile.scss";
import { Skeleton } from "primereact/skeleton";
import { DataTable } from "primereact/datatable";
import { Menu } from "primereact/menu";
import CustomeButton from "~/components/Button";
import useTenantCustomerPortfolio from "./useTenantCustomerPortfolio";
import ReactCountryFlag from "react-country-flag";
import useDateTime from "~/hooks/useDateTime";
import ManageEntityServiceLineModal from "../../modals/ManageEntityServiceLineModal";
import { Entity } from "../../types/tenants";
import ToastMessage from "~/components/ToastMessage";
import UnassignModal from "../../modals/UnassignModal";
import { useParams } from "react-router-dom";
import TenantAuditSidebar from "~/components/audit-sidebar/TenantAuditSidebar";
import AdminOnly from "~/components/AdminOnly";
import { useQueryClient } from "@tanstack/react-query";
import { useTenantEntityAccessAudit } from "~/api/tenants/hooks/useTenantEntityAccessAudit";

const TenantCustomerPortfolio = () => {
  const {
    loading,
    contextMenu,
    treeData,
    page,
    rows,
    totalRecords,
    onPageChange,
    onSortChange,
    sortBy,
    sortOrder,
    deleteLspEntityAccess,
    fetchTenantEntities,
  } = useTenantCustomerPortfolio();
  const { formatDateTime } = useDateTime();
  const [displayAssignModal, setDisplayAssignModal] = useState(false);
  const [displayUnassignModal, setDisplayUnassignModal] = useState(false);
  const [unassignLoading, setUnassignloading] = useState(false);
  const [isEditModal, setIsEditModal] = useState(false);
  const [entitySelected, setEntitySelected] = useState<Entity | null>(null);
  const toastRef = useRef(null);
  const { id } = useParams();
  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const queryClient = useQueryClient();

  const editBtnHandler = (rowData) => {
    setEntitySelected(rowData.data);
    setIsEditModal(true);
    setDisplayAssignModal(true);
  };

  const deleteBtnHandler = (rowData) => {
    setEntitySelected(rowData.data);
    setDisplayUnassignModal(true);
  };

  const closeModalHandler = () => {
    setDisplayAssignModal(false);
    setIsEditModal(false);
  };

  const closeUnassignModalHanlder = () => {
    setDisplayUnassignModal(false);
    setEntitySelected(null);
  };

  const unAssignEntityHandler = async () => {
    try {
      setUnassignloading(true);
      if (id && entitySelected) {
        const response = await deleteLspEntityAccess(id, entitySelected?.id);
        if (response.success) {
          toastRef?.current?.show({
            severity: "success",
            icon: "pi pi-check",
            summary: `'${entitySelected?.name}' has been successfully unassigned from the Local Team`,
            life: 5000,
          });
          closeUnassignModalHanlder();
          await fetchTenantEntities({
            pageIndex: 0,
            pageSize: 10,
            sortBy: "name",
            sortOrder: "asc",
            skipCache: true,
          });
          // Invalidate audit logs cache to refresh after deletion
          queryClient.invalidateQueries({
            queryKey: ["tenant-entity-access-audit", id],
          });
        }
      }
    } catch (error) {
      console.error(error);
      toastRef?.current?.show({
        severity: "error",
        icon: "pi pi-times-circle",
        summary: "Oops! Something went wrong while removing access.",
        detail: "Please try again.",
        life: 5000,
      });
    } finally {
      setUnassignloading(false);
    }
  };

  const expirationBodyTemplate = (rowData) => {
    if (rowData.data.level === "entity") {
      const expDate =
        rowData.data.expirationDate &&
        new Date(rowData.data.expirationDate).toLocaleDateString("en-GB");
      return (
        <div className="flex align-items-center justify-content-between">
          {rowData.data.expirationDate ? expDate : ""}
        </div>
      );
    }
    return "";
  };

  const EntityIds = useMemo(() => {
    return treeData.map((item) => item.data.id);
  }, [treeData]);

  const auditLabels = () => ({
    create: "Entity Access Added",
    update: "Entity Access Updated",
    delete: "Entity Access Removed",
  });
  return (
    <>
      <Card className="gen-card customer-portfolio">
        <div className="formgrid grid flex justify-content-between mb-4 align-items-center">
          <div className="">
            <p className="heading-main m-0 mb-2">Customer Portfolio</p>
            <p className="description m-0">
              View all customer entities and services assigned to this Local
              Team.
            </p>
          </div>
          <div className="flex gap-2">
            <AdminOnly>
              <CustomeButton
                label="View History"
                variant="outlined"
                onClick={() => setAuditSidebarVisible(true)}
                icon="pi pi-history"
                iconPos="right"
              />
            </AdminOnly>
            <CustomeButton
              label={"Add New Entity Access"}
              variant={"contained"}
              onClick={() => {
                setDisplayAssignModal(true);
              }}
            />
          </div>
        </div>
        {loading ? (
          <DataTable
            value={Array(5).fill({})}
            tableStyle={{ minWidth: "100%" }}
            className="datatable-right-align"
          >
            <Column
              header="Entity Name"
              sortable
              className="table-col-width"
              body={<Skeleton />}
            />
            <Column
              header="Expiration Date"
              sortable
              className="table-col-width"
              body={<Skeleton />}
            />
          </DataTable>
        ) : (
          <TreeTable
            className="mt-3"
            first={page * rows}
            rows={rows}
            totalRecords={totalRecords}
            onPage={onPageChange}
            value={treeData}
            paginator={totalRecords > 10}
            lazy
            rowsPerPageOptions={[10, 20, 30]}
            onSort={onSortChange}
            sortField={sortBy}
            sortOrder={sortOrder}
            emptyMessage="No customer entities or services have been assigned yet."
          >
            <Column
              field="name"
              header="Entity Name"
              body={(rowData) => {
                const { name, country } = rowData.data;
                return (
                  <span className="inline-flex align-items-center gap-2">
                    <ReactCountryFlag
                      countryCode={country}
                      svg
                      style={{ width: "18px", height: "18px" }}
                    />
                    <span className="entity-name-text">{name ? name : ""}</span>
                    {country && (
                      <span className="country-code">({country})</span>
                    )}
                  </span>
                );
              }}
              expander
              sortable
              style={{ width: "50%" }}
            />

            <Column
              field="expiry"
              header="Expiration Date"
              body={(rowData) => expirationBodyTemplate(rowData)}
              sortable
              style={{ width: "25%" }}
            />
            <Column
              body={(rowData) => {
                if (rowData.data.level !== "entity") return null;

                return (
                  <div className="flex flex-row-reverse">
                    <Menu
                      popup
                      ref={(el) => {
                        if (rowData.key) {
                          contextMenu.current[rowData.key] = el;
                        }
                      }}
                      id="process_actions_menu"
                      model={[
                        {
                          label: (
                            <div className="global-entities-context-menu-item">
                              <i className="pi pi-pencil" />
                              Edit Access
                            </div>
                          ),
                          command: () => {
                            editBtnHandler(rowData);
                          },
                        },
                        {
                          label: (
                            <div className="global-entities-context-menu-item">
                              <i className="pi pi-trash" />
                              Remove Access
                            </div>
                          ),
                          command: () => {
                            deleteBtnHandler(rowData);
                          },
                        },
                      ]}
                    />

                    {/* Button that opens the menu */}
                    <CustomeButton
                      icon="pi pi-ellipsis-v"
                      variant="no-outlined"
                      onClick={(event) => {
                        contextMenu.current[rowData.key].toggle(event);
                      }}
                    />
                  </div>
                );
              }}
              style={{ width: "25%" }}
            />
          </TreeTable>
        )}
      </Card>
      {displayAssignModal && (
        <ManageEntityServiceLineModal
          visible={displayAssignModal}
          closeModal={closeModalHandler}
          isEdit={isEditModal}
          entity={entitySelected}
          toastRef={toastRef}
          assignedEntities={EntityIds}
          fetchTenantEntities={fetchTenantEntities}
        />
      )}
      {displayUnassignModal && (
        <UnassignModal
          visible={displayUnassignModal}
          closeModal={closeUnassignModalHanlder}
          unAssignHandler={unAssignEntityHandler}
          loading={unassignLoading}
          title={`Are you sure you want to remove access to ‘${entitySelected?.name}’ from this Local Team?`}
          description="This action will permanently remove the LSP's access to this customer entity and all related service lines. Do you want to continue?"
          toastRef={toastRef}
          actionBtnlabel="Yes, Unassign"
        />
      )}
      <ToastMessage ref={toastRef} />
      {isAuditSidebarVisible && (
        <TenantAuditSidebar
          heading="Customer Portfolio"
          tenantId={id!}
          setSidebarToggle={setAuditSidebarVisible}
          sidebarToggle={isAuditSidebarVisible}
          labels={auditLabels}
          useAuditHook={useTenantEntityAccessAudit}
        />
      )}
    </>
  );
};

export default TenantCustomerPortfolio;
